import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, TextField, Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";
import { setTemporaryUserPassword } from "./api/tenantApi";

export default function TemporaryPasswordDialog({ open, user, tenantId, tenantName, onClose }) {
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!open) { setPassword(""); setShow(false); setBusy(false); }
  }, [open]);

  const generate = async () => {
    if (!user?.id || !tenantId) return;
    setBusy(true);
    try {
      const response = await setTemporaryUserPassword(user.id, tenantId);
      setPassword(response?.temporaryPassword || "");
      setShow(true);
      toast.success("Temporary password created");
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.response?.data?.details || "Could not set a temporary password");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      toast.success("Temporary password copied");
    } catch (_) { toast.error("Could not copy the password"); }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 750 }}>{password ? "Temporary password" : "Set temporary password"}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: "#475569", mb: 2 }}>
          {user?.name || user?.email} · {tenantName}
        </Typography>
        {!password ? (
          <Alert severity="warning">
            This replaces the user’s current password immediately. Existing passwords cannot be retrieved. The new password will be shown only once.
          </Alert>
        ) : (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Password updated. Copy it now—CrediArc will not store or show it again after this window closes.
            </Alert>
            <TextField
              fullWidth
              label="New temporary password"
              value={password}
              type={show ? "text" : "password"}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label={show ? "Hide password" : "Show password"} onClick={() => setShow((value) => !value)} edge="end">
                      {show ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button startIcon={<ContentCopyIcon />} onClick={copy} sx={{ textTransform: "none" }}>Copy password</Button>
            </Box>
            <Typography sx={{ fontSize: 12, color: "#64748b", mt: 1 }}>
              Ask the user to change this temporary password using “Send password reset” after access is restored.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none" }}>{password ? "Done" : "Cancel"}</Button>
        {!password && <Button onClick={generate} variant="contained" disabled={busy} sx={{ textTransform: "none" }}>{busy ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Setting…</> : "Set password"}</Button>}
      </DialogActions>
    </Dialog>
  );
}
